<template>
  <Dialog v-model:visible="visible" modal :resizable="true" :closable="true" class="w-[90%] max-w-[1200px]"
    header="Print Service Invoice">
    <!-- Main Content -->
    <div class="flex flex-col md:flex-row gap-4 h-full">

      <!-- =======================
           PRINTABLE CANVAS
      ======================== -->
      <div class="flex justify-center items-center flex-1 overflow-auto
               bg-gray-100 dark:bg-gray-800
               border dark:border-gray-700
               p-3 rounded">
        <div id="printable" ref="canvas" class="relative bg-white dark:bg-gray-900
                 mx-auto shadow-md dark:shadow-gray-700
                 transition-all duration-300 ease-out
                 hover:shadow-lg dark:hover:shadow-gray-600" :style="{

                  width:
                    (positionPresets[selectedTemplate]?.size?.width ?? 643) +
                    'px',

                  height:
                    (positionPresets[selectedTemplate]?.size?.height ?? 756) +
                    'px',

                  transformOrigin: 'top left',
                }" @mousemove="onDrag" @mouseup="stopDrag" @mouseleave="stopDrag" @touchmove.prevent="onDrag"
          @touchend="stopDrag">

          <!-- =========================
               TEMPLATE BACKGROUND
          ========================== -->
          <img v-if="positionPresets[selectedTemplate]" class="print:hidden"
            :src="positionPresets[selectedTemplate].image" :alt="selectedTemplate" :style="{
              width:
                (positionPresets[selectedTemplate]?.size?.width ?? 643) +
                'px',

              height:
                (positionPresets[selectedTemplate]?.size?.height ?? 756) +
                'px',
            }" />

          <!-- =========================
               DRAGGABLE FIELDS
          ========================== -->
          <div v-for="field in draggableFields" :key="field.key" class="absolute select-none rounded
                   print:bg-transparent
                   print:border-0" :class="[
                    field.fontClass,

                    editMode
                      ? 'cursor-grab border border-dashed border-blue-600 bg-blue-100/30 hover:bg-blue-200/40'
                      : 'cursor-default border border-transparent bg-transparent',

                    isDragging && activeField === field.key
                      ? 'transition-none'
                      : 'transition-all duration-200 ease-out',

                    dragAllMode
                      ? 'border-dashed border-purple-500 bg-purple-100/20'
                      : '',

                    (field.key === 'tin' || field.key === 'tinNew') &&
                      field.value === '0'
                      ? 'print:hidden'
                      : '',
                  ]" :style="{
                    top:
                      field.useDynamicY && wrapReverse
                        ? dynamicSoldToY(
                          field.baseTop ??
                          positions[field.key]?.y ??
                          0
                        ) + 'px'
                        : (positions[field.key]?.y ?? 0) + 'px',

                    left:
                      (positions[field.key]?.x ?? 0) + 'px',

                    width: field.width,
                    textAlign: field.textAlign,
                    padding: '2px',
                  }" @mousedown="
                    editMode
                      ? startDrag($event, field.key)
                      : null
                    " @touchstart.prevent="
                      editMode
                        ? startDrag($event, field.key)
                        : null
                      ">
            <span class="inline-block text-black font-semibold text-xs
                     transition-transform duration-200 ease-in-out">
              {{ editMode ? field.key : field.value }}
            </span>
          </div>
        </div>
      </div>

      <!-- =========================
           CONTROLS PANEL
      ========================== -->
      <div class="w-full md:w-64
               bg-gray-50 dark:bg-gray-900
               border dark:border-gray-700
               p-4 rounded-lg
               flex flex-col
               max-h-[50vh]">

        <!-- Scrollable Content -->
        <div class="flex-1 overflow-y-auto">

          <!-- =========================
               HEADER
          ========================== -->
          <div class="flex items-center justify-between
                   mb-3
                   border-b border-slate-300 dark:border-gray-600
                   pb-2">
            <h3 class="font-semibold text-lg
                     text-gray-900 dark:text-gray-100">
              Editor Panel
            </h3>
          </div>

          <!-- =========================
               TEMPLATE SELECTOR
          ========================== -->
          <div class="flex flex-col mb-4">

            <label class="text-sm font-medium
                     text-gray-800 dark:text-gray-300
                     mb-1">
              Template
            </label>

            <select v-model="selectedTemplate" @change="saveSelectedTemplate" class="border rounded px-2 py-1 text-sm
                     bg-white dark:bg-gray-800
                     dark:text-gray-100
                     dark:border-gray-600">
              <option v-for="(preset, key) in positionPresets" :key="key" :value="key">
                {{ key }}
              </option>
            </select>

          </div>

          <!-- =========================
               EDIT MODE
          ========================== -->
          <div class="border-t border-slate-300 dark:border-gray-600
                   pt-3 mt-3">

            <!-- Edit Mode Toggle -->
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium
                       text-gray-800 dark:text-gray-300">
                Edit Mode
              </span>

              <label class="inline-flex relative items-center cursor-pointer">
                <input type="checkbox" v-model="editMode" class="sr-only peer" />

                <div class="w-11 h-6
                         bg-gray-200 dark:bg-gray-700
                         peer-focus:outline-none
                         peer-focus:ring-2
                         peer-focus:ring-brand_orange2
                         rounded-full
                         peer
                         peer-checked:bg-brand_orange3
                         transition-all"></div>

                <div class="absolute left-1 top-1
                         w-4 h-4
                         bg-white dark:bg-gray-200
                         rounded-full
                         transition-all
                         peer-checked:translate-x-5"></div>
              </label>
            </div>

            <!-- =========================
                 EDIT MODE CONTENT
            ========================== -->
            <div v-if="editMode" class="space-y-4 mt-3">

              <!-- =======================
                   DRAG ALL TOGGLE
              ======================== -->
              <div class="flex items-center justify-between">
                <div class="flex flex-col">

                  <span class="text-sm font-medium
                           text-gray-800 dark:text-gray-300">
                    Drag All Fields
                  </span>

                  <span class="text-[10px]
                           text-gray-500 dark:text-gray-400">
                    Move the entire layout together
                  </span>

                </div>

                <label class="inline-flex relative items-center cursor-pointer">
                  <input type="checkbox" v-model="dragAllMode" class="sr-only peer" />

                  <div class="w-11 h-6
                           bg-gray-200 dark:bg-gray-700
                           peer-focus:outline-none
                           peer-focus:ring-2
                           peer-focus:ring-purple-400
                           rounded-full
                           peer
                           peer-checked:bg-purple-500
                           transition-all"></div>

                  <div class="absolute left-1 top-1
                           w-4 h-4
                           bg-white dark:bg-gray-200
                           rounded-full
                           transition-all
                           peer-checked:translate-x-5"></div>
                </label>

              </div>

              <!-- =======================
                   WRAP REVERSE
              ======================== -->
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium
                         text-gray-800 dark:text-gray-300">
                  Sold To Wrap Reverse
                </span>

                <label class="inline-flex relative items-center cursor-pointer">
                  <input type="checkbox" v-model="wrapReverse" class="sr-only peer" />

                  <div class="w-11 h-6
                           bg-gray-200 dark:bg-gray-700
                           peer-focus:outline-none
                           peer-focus:ring-2
                           peer-focus:ring-brand_orange2
                           rounded-full
                           peer
                           peer-checked:bg-brand_orange3
                           transition-all"></div>

                  <div class="absolute left-1 top-1
                           w-4 h-4
                           bg-white dark:bg-gray-200
                           rounded-full
                           transition-all
                           peer-checked:translate-x-5"></div>
                </label>

              </div>

              <!-- Divider -->
              <div class="border-t border-slate-200
                       dark:border-gray-700"></div>

              <!-- =========================
                   EDIT ACTIONS
              ========================== -->
              <div>

                <p class="text-xs uppercase font-semibold
                         text-slate-500 dark:text-gray-400
                         mb-2">
                  Actions
                </p>

                <div class="flex flex-col gap-2">

                  <!-- Save -->
                  <button class="bg-green-500 text-white
                           px-3 py-2 rounded
                           hover:bg-green-600
                           dark:hover:bg-green-700
                           transition" @click="saveTemplatePositions">
                    Save Changes
                  </button>

                  <!-- Reset -->
                  <button class="bg-gray-400 text-white
                           px-3 py-2 rounded
                           hover:bg-gray-500
                           dark:bg-gray-700
                           dark:hover:bg-gray-600
                           transition" @click="resetTemplate">
                    Restore Default
                  </button>

                </div>

              </div>

            </div>
          </div>

        </div>

        <!-- =========================
             FIXED BOTTOM ACTION
        ========================== -->
        <div class="pt-3 mt-2
                 border-t border-slate-300 dark:border-gray-600">

          <button :disabled="editMode" @click="printPage()" class="px-3 py-2 rounded w-full
                   transition font-medium text-white
                   bg-brand_orange2
                   hover:bg-orange-600
                   dark:hover:bg-orange-500
                   disabled:bg-gray-300
                   disabled:text-gray-600
                   disabled:cursor-not-allowed
                   disabled:hover:bg-gray-300">
            {{ editMode ? "Disable Edit to Print" : "Print Invoice" }}
          </button>

        </div>

      </div>
    </div>
  </Dialog>
</template>

<script>

import { ref, computed, watch, onMounted } from "vue";

// Composables
import { useDrag } from "@/composables/useDrag";
import { usePrint } from "@/composables/usePrint";
import { useDynamicTopPositioning } from "@/composables/usePrintDynamicTopPosition";

// Config
import { invoiceTemplates } from "@/config/invoice/templates";
import { invoiceFields } from "@/config/invoice/fields";

export default {
  props: {
    serviceInvoiceData: {
      type: Object,
      required: true,
    },

    modelValue: {
      type: Boolean,
      required: true,
    },
  },

  emits: ["update:modelValue"],

  setup(props, { emit }) {
    // ============================================================
    // DIALOG
    // ============================================================

    const visible = computed({
      get: () => props.modelValue,
      set: (value) => emit("update:modelValue", value),
    });

    // ============================================================
    // STATE
    // ============================================================

    const selectedTemplate = ref("Service_Invoice_1");

    const editMode = ref(false);

    // Drag all fields at once
    const dragAllMode = ref(false);

    // Sold To wrap reverse
    const wrapReverse = ref(true);

    // ============================================================
    // TEMPLATE CONFIG
    // ============================================================

    const positionPresets = ref(
      structuredClone(invoiceTemplates)
    );

    // ============================================================
    // CURRENT POSITIONS
    // ============================================================

    const positions = ref({});

    // ============================================================
    // HELPERS
    // ============================================================

    const getCurrentTemplate = () => {
      return (
        positionPresets.value[selectedTemplate.value] || {
          fields: {},
          size: {
            width: 643,
            height: 756,
          },
        }
      );
    };

    const loadCurrentTemplatePositions = () => {
      const template = getCurrentTemplate();

      const nextPositions = {};

      Object.entries(template.fields || {}).forEach(
        ([fieldKey, config]) => {
          nextPositions[fieldKey] = {
            x: config.x ?? 0,
            y: config.y ?? 0,
          };
        }
      );

      positions.value = nextPositions;
    };

    // ============================================================
    // DRAGGABLE FIELDS
    // ============================================================

    const draggableFields = computed(() => {
      const template = getCurrentTemplate();

      return Object.entries(template.fields || {})
        .map(([fieldKey, templateConfig]) => {
          const fieldDefinition = invoiceFields[fieldKey];

          if (!fieldDefinition) {
            console.warn(
              `[Invoice Template] Unknown field "${fieldKey}" in template "${selectedTemplate.value}".`
            );

            return null;
          }

          return {
            key: fieldKey,

            // Get the actual value from the current invoice data
            value: fieldDefinition.getValue(
              props.serviceInvoiceData
            ),

            // Template override -> field default
            width:
              templateConfig.width ??
              fieldDefinition.width,

            // Template override -> field default
            fontClass:
              typeof templateConfig.fontClass === "function"
                ? templateConfig.fontClass(
                  props.serviceInvoiceData
                )
                : templateConfig.fontClass ??
                (
                  typeof fieldDefinition.fontClass === "function"
                    ? fieldDefinition.fontClass(
                      props.serviceInvoiceData
                    )
                    : fieldDefinition.fontClass
                ),

            // Template override -> field default
            textAlign:
              templateConfig.textAlign ??
              fieldDefinition.textAlign,

            // Template override -> field default
            baseTop:
              templateConfig.baseTop ??
              fieldDefinition.baseTop ??
              0,

            // Template override -> field default
            useDynamicY:
              templateConfig.useDynamicY ??
              fieldDefinition.useDynamicY ??
              false,
          };
        })
        .filter(Boolean);
    });

    // ============================================================
    // WATCHERS
    // ============================================================

    watch(selectedTemplate, () => {
      loadCurrentTemplatePositions();
    });

    watch(visible, (value) => {
      if (value) {
        loadCurrentTemplatePositions();
      }
    });

    watch(editMode, (enabled) => {
      if (!enabled) {
        dragAllMode.value = false;
      }
    });

    watch(dragAllMode, (enabled) => {
      if (enabled) {
        editMode.value = true;
      }
    });

    // ============================================================
    // COMPOSABLES
    // ============================================================

    const { printPage } = usePrint();

    const { dynamicSoldToY } =
      useDynamicTopPositioning(props);

    const {
      startDrag,
      onDrag,
      stopDrag,
      isDragging,
      activeField,
    } = useDrag(
      positions,
      editMode,
      dragAllMode
    );

    // ============================================================
    // LOCAL STORAGE
    // ============================================================

    const saveSelectedTemplate = () => {
      localStorage.setItem(
        "selectedInvoiceTemplate",
        JSON.stringify(selectedTemplate.value)
      );
    };

    // ============================================================
    // SAVE WRAP REVERSE SETTING
    // ============================================================

    const saveWrapReverse = () => {
      localStorage.setItem(
        "invoiceWrapReverse",
        JSON.stringify(wrapReverse.value)
      );
    };

    // ============================================================
    // LOAD WRAP REVERSE SETTING
    // ============================================================

    const loadWrapReverse = () => {
      const saved =
        localStorage.getItem(
          "invoiceWrapReverse"
        );

      // No saved value yet, keep the default
      if (saved === null) {
        return;
      }

      try {
        wrapReverse.value = JSON.parse(saved);
      } catch (error) {
        console.error(
          "Failed to load wrap reverse setting:",
          error
        );
      }
    };

    // ============================================================
    // SAVE TEMPLATE POSITIONS
    // ============================================================

    const saveTemplatePositions = () => {
      const template =
        positionPresets.value[
        selectedTemplate.value
        ];

      if (!template) {
        return;
      }

      Object.entries(positions.value).forEach(
        ([fieldKey, position]) => {
          const field =
            template.fields?.[fieldKey];

          if (!field) {
            return;
          }

          field.x = position.x;
          field.y = position.y;
        }
      );

      // Save current positions
      localStorage.setItem(
        "invoiceTemplatePositions",
        JSON.stringify(
          positionPresets.value
        )
      );

      // Save wrap reverse setting
      saveWrapReverse();

      if (
        !localStorage.getItem(
          "invoiceTemplateDefaults"
        )
      ) {
        localStorage.setItem(
          "invoiceTemplateDefaults",
          JSON.stringify(
            invoiceTemplates
          )
        );
      }

      alert(
        "All template positions and settings saved!"
      );
    };

    // ============================================================
    // RESET TEMPLATE
    // ============================================================

    const resetTemplate = () => {
      const defaults =
        localStorage.getItem(
          "invoiceTemplateDefaults"
        );

      if (!defaults) {
        alert(
          "No default templates found in storage."
        );

        return;
      }

      try {
        positionPresets.value =
          JSON.parse(defaults);

        loadCurrentTemplatePositions();

        localStorage.setItem(
          "invoiceTemplatePositions",
          JSON.stringify(
            positionPresets.value
          )
        );

        alert(
          "Template positions have been reset to default!"
        );
      } catch (error) {
        console.error(
          "Failed to reset templates:",
          error
        );

        alert(
          "Failed to reset templates - invalid default data."
        );
      }
    };

    // ============================================================
    // LOAD SAVED TEMPLATE POSITIONS
    // ============================================================

    const loadSavedTemplatePosition = () => {
      const saved =
        localStorage.getItem(
          "invoiceTemplatePositions"
        );

      const defaults =
        localStorage.getItem(
          "invoiceTemplateDefaults"
        );

      try {
        if (saved) {
          positionPresets.value =
            JSON.parse(saved);
        } else {

          localStorage.setItem(
            "invoiceTemplatePositions",
            JSON.stringify(
              positionPresets.value
            )
          );

          if (!defaults) {
            localStorage.setItem(
              "invoiceTemplateDefaults",
              JSON.stringify(
                invoiceTemplates
              )
            );
          }
        }
      } catch (error) {
        console.error(
          "Failed to load template positions:",
          error
        );
      }

      loadCurrentTemplatePositions();
    };

    // ============================================================
    // LOAD SELECTED TEMPLATE
    // ============================================================

    const loadSelectedTemplate = () => {
      const saved =
        localStorage.getItem(
          "selectedInvoiceTemplate"
        );

      if (!saved) {
        return null;
      }

      try {
        const parsed =
          JSON.parse(saved);

        if (
          positionPresets.value[parsed]
        ) {
          selectedTemplate.value =
            parsed;
        }

        return parsed;
      } catch (error) {
        console.error(
          "Failed to parse selected template:",
          error
        );

        return null;
      }
    };

    // ============================================================
    // LIFECYCLE
    // ============================================================

    onMounted(() => {
      loadSavedTemplatePosition();
      loadSelectedTemplate();
      loadWrapReverse();
      loadCurrentTemplatePositions();
    });

    // ============================================================
    // RETURN
    // ============================================================

    return {
      // Data
      positions,
      positionPresets,
      draggableFields,
      selectedTemplate,

      // State
      visible,
      editMode,
      dragAllMode,
      wrapReverse,

      // Drag state
      isDragging,
      activeField,

      // Composables
      onDrag,
      stopDrag,
      startDrag,
      printPage,

      // Dynamic positioning
      dynamicSoldToY,

      // Template actions
      resetTemplate,
      saveTemplatePositions,
      saveSelectedTemplate,
      loadSelectedTemplate,
      loadSavedTemplatePosition,

      // Wrap reverse
      saveWrapReverse,
      loadWrapReverse,
    };
  },
};
</script>
